<template>
  <transition
    name="toast-fade"
    @before-leave="onClose"
    @after-leave="$emit('destroy')"
  >
    <div
      v-show="visible"
      :class="['toast', placementClass, { 'with-close-icon': showCloseIcon }]"
      :style="customStyle"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      @mouseenter="clearTimer"
      @mouseleave="startTimer"
      @click="handleOnClick"
    >
      <div class="toast-content">
        <slot>{{ message }}</slot>
        <a
          v-if="ctaText && ctaLink"
          :href="ctaLink"
          target="_blank"
          :class="['cta-link', { 'with-close-icon': showCloseIcon }]"
          @click.stop="handleCtaClick"
        >{{ ctaText }}</a>
        <button
          v-if="showCloseIcon"
          @click.stop="handleCloseClick"
        >
          <Icon
            theme="light"
            size="s"
            class="close-icon"
            :icon="csgClose"
          />
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  defineComponent, onMounted, ref, computed,
} from 'vue';
import { csgClose } from '@csgSvgIcons/icons';
import Icon from '../icon';

export default defineComponent({
  name: 'Toast',
  props: {
    duration: {
      type: Number,
      default: 3000,
    },
    message: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    onClick: {
      type: Function,
      default: () => {},
    },
    onTimeout: {
      type: Function,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    toastContainerId: {
      type: String,
      default: '',
    },
    offset: {
      type: Number,
      default: 20,
    },
    placement: {
      type: String,
      validator: (val) => [
        'top',
        'bottom',
        'bottom-left',
        'bottom-right',
        'left',
        'right',
        'center',
      ].includes(val),
      default: 'top',
    },
    showCloseIcon: {
      type: Boolean,
      default: false,
    },
    ctaText: {
      type: String,
      default: '',
    },
    ctaLink: {
      type: String,
      default: '',
    },
    onCtaClick: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      csgClose,
    };
  },
  components: {
    Icon,
  },
  emits: { destroy: () => true },
  setup(props) {
    let timer;

    const visible = ref(false);

    const close = () => {
      visible.value = false;
      setTimeout(() => props.onClose(), 0);
    };

    const startTimer = () => {
      if (props.duration > 0) {
        timer = setTimeout(() => {
          if (visible.value) {
            props.onTimeout();
            close();
          }
        }, props.duration);
      }
    };

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const handleOnClick = () => {
      props.onClick();
      close();
    };

    const handleCtaClick = (event) => {
      event.stopPropagation();
      props.onCtaClick();
    };

    const handleCloseClick = (event) => {
      event.stopPropagation();
      close();
    };

    const placementClass = computed(() => {
      if (props.placement.indexOf('right') > -1) {
        return 'right';
      }
      if (props.placement.indexOf('left') > -1) {
        return 'left';
      }
      return props.placement;
    });

    const verticalProperty = computed(() => {
      if (props.placement === 'center') {
        return 'center';
      }
      if (
        props.placement.indexOf('top') > -1
        || props.placement === 'left'
        || props.placement === 'right'
      ) {
        return 'top';
      }
      return 'bottom';
    });

    const customStyle = computed(() => ({
      [verticalProperty.value]: `${props.offset}px`,
    }));

    onMounted(() => {
      startTimer();
      visible.value = true;
    });

    return {
      visible,
      customStyle,
      placementClass,
      close,
      clearTimer,
      startTimer,
      handleOnClick,
      handleCtaClick,
      handleCloseClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.toast {
  border-radius: rem-calc(5px);
  position: fixed;
  padding: rem-calc(12px);
  background-color: $black;
  transition: opacity 0.2s, transform 0.2s, left 0.2s, right 0.2s, top 0.2s,
    bottom 0.2s;
  color: $white;
  z-index: 9999;
  width: max-content;
  max-width: 80%;
  text-align: center;

  &.right {
    right: rem-calc(16px);
  }

  &.left {
    left: rem-calc(16px);
  }
  &.top {
    left: 50%;
    transform: translateX(-50%);
  }

  &.bottom {
    left: 50%;
    transform: translateX(-50%);
  }
  &.center {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &.with-close-icon {
    padding: rem-calc(20px);
  }

  .toast-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.cta-link {
  margin-left: rem-calc(12px);
  color: $white;
  cursor: pointer;

  &.with-close-icon {
    margin-right: rem-calc(12px);
  }
}

.close-icon {
  background: none;
  border: none;
  cursor: pointer;
  -webkit-filter: brightness(0) invert(1);
  filter: brightness(0) invert(1);
  margin-left: rem-calc(12px);
}
</style>

<style lang="scss" brand="bcom" scoped>
.toast {
  border-radius: 0;
}
</style>
